import type { Clube, Jogador, Posicao } from './tipos';
import type { Rng } from './rng';

/**
 * Fórmulas econômicas. A forma segue a engenharia reversa do jogo original (ver ../../../conhecimento/04 e 05),
 * mas as constantes de escala são próprias e foram calibradas por scripts/calibrar.ts.
 */

/**
 * Escala do salário mensal. Com 1 o valor é igual ao do original (um goleiro de força 46 e 36 anos ganha ≈ 92 mil por mês).
 * O peso dos salários no caixa é ajustado por MESES_DE_FOLHA, não por esta escala.
 */
export const ESCALA_SALARIO = 1;

/** Quantos salários mensais o clube paga ao longo de uma temporada só de liga (o original tem mais competições). */
export const MESES_DE_FOLHA = 6;

const DESCONTO_POSICAO: Record<Posicao, number> = { G: 70, L: 30, Z: 40, M: 0, A: 50 };

function baseSalarial(nivel: number): number {
  if (nivel >= 17) return 750;
  if (nivel >= 14) return 600;
  if (nivel >= 11) return 500;
  return 400;
}

/** Salário mensal (o original usa valor semanal × 4 quando a opção "salário mensal" está ligada). */
export function calcularSalario(overall: number, idade: number, posicao: Posicao, nivelClube: number): number {
  const base = baseSalarial(nivelClube) - DESCONTO_POSICAO[posicao];
  let v = overall * 2 * Math.round(0.5 * base);
  if (idade >= 32) v -= (idade - 32) * 300;
  v = Math.max(v, 500);
  return Math.round(v * 4 * ESCALA_SALARIO);
}

// ---- Valor do jogador e força: uma só curva, nos dois sentidos --------------------------------------
// O valor de mercado cresce de forma exponencial com a força (cada K pontos de força multiplicam o valor por e ≈ 2,7), como nos
// dados reais do Transfermarkt. A mesma curva converte valor em força (carga inicial da Série A) e força em valor (o resto do jogo),
// por isso o valor e a força nunca se contradizem. Idade e posição mudam o preço sem mudar a qualidade: o jovem custa mais pelo
// futuro, o veterano menos, e goleiro vale menos que atacante com a mesma força.
const FORCA_REF = 41.4;
const VALOR_REF = 2_300_000;
const PONTOS_POR_E = 7.5;
const FORCA_MIN = 20;
const FORCA_MAX = 90;

const FATOR_POSICAO_VALOR: Record<Posicao, number> = { G: 0.6, Z: 0.9, L: 0.95, M: 1, A: 1.15 };

/** Quanto o preço do jogador passa (ou fica abaixo) do que a força dele valeria aos 25 anos. */
function premioIdade(idade: number): number {
  if (idade <= 19) return 1.9;
  if (idade <= 21) return 1.6;
  if (idade <= 23) return 1.3;
  if (idade <= 26) return 1;
  if (idade <= 28) return 0.9;
  if (idade === 29) return 0.8;
  if (idade === 30) return 0.68;
  if (idade === 31) return 0.55;
  if (idade === 32) return 0.45;
  if (idade === 33) return 0.35;
  if (idade === 34) return 0.28;
  return 0.2;
}

/** Valor (R$) de um jogador com essa força, idade e posição. `_nivelClube` fica por compatibilidade: o preço não depende do clube. */
export function calcularValor(overall: number, idade: number, _nivelClube?: number, posicao?: Posicao): number {
  const base = VALOR_REF * Math.exp((overall - FORCA_REF) / PONTOS_POR_E);
  const v = base * premioIdade(idade) * (posicao ? FATOR_POSICAO_VALOR[posicao] : 1);
  return Math.max(50_000, Math.round(v / 10_000) * 10_000);
}

/** Força que corresponde a um valor de mercado (inversa de calcularValor). */
export function overallDoValor(valor: number, idade: number, posicao?: Posicao): number {
  const base = Math.max(1, valor) / (premioIdade(idade) * (posicao ? FATOR_POSICAO_VALOR[posicao] : 1));
  const o = FORCA_REF + PONTOS_POR_E * Math.log(base / VALOR_REF);
  return Math.round(Math.min(FORCA_MAX, Math.max(FORCA_MIN, o)));
}

export function folhaMensal(clube: Clube): number {
  return clube.elenco.reduce((s, j) => s + j.salario, 0);
}

/** Pagamento por rodada: a folha da temporada (MESES_DE_FOLHA salários mensais) dividida pelas rodadas. */
export function folhaPorRodada(clube: Clube, rodadas = 38, meses = MESES_DE_FOLHA): number {
  return Math.round((folhaMensal(clube) * meses) / rodadas);
}

// ---- Bilheteria (doc 05) -------------------------------------------------------------------------

/** Preços sugeridos para jogos de liga da 1ª divisão (aq.qK[1][1] do original): Geral, Arquibancada, Cadeira, Camarote. */
export const PRECOS_SUGERIDOS: [number, number, number, number] = [10, 15, 25, 80];
/** @deprecated nome antigo; use PRECOS_SUGERIDOS */
export const PRECOS_PADRAO = PRECOS_SUGERIDOS;
const PARTICIPACAO_SETOR = [0.15, 0.75, 0.09, 0.01];
const PUBLICO_BASE: number[][] = [
  [200, 500, 50, 0],
  [1000, 5000, 1200, 20],
  [2000, 10000, 1500, 50],
  [4000, 20000, 2500, 300],
  [4500, 30000, 3500, 400],
  [5000, 40000, 5500, 500],
];
const SORTEIO_PUBLICO = [10, 100, 300, 400, 500];
/** Acima de 1: preço maior rende menos no total (a renda só sobe enquanto o estádio não enche). */
const ELASTICIDADE_PRECO = 1.25;

export interface Bilheteria {
  publico: [number, number, number, number];
  total: number;
  renda: number;
}

export function calcularBilheteria(mandante: Clube, visitante: Clube, rng: Rng, divisao = 1): Bilheteria {
  const capacidade = mandante.capacidade;
  const setores = PARTICIPACAO_SETOR.map((p) => Math.round(capacidade * p));
  const base = PUBLICO_BASE[Math.min(5, Math.max(0, mandante.reputacao))] as number[];
  const dif = visitante.reputacao - mandante.reputacao;
  const ajusteRep = Math.sign(dif) * Math.min(0.25, Math.abs(dif) * 0.05);
  const sorteioMax = SORTEIO_PUBLICO[Math.min(4, divisao)] as number;
  const publico: number[] = [0, 0, 0, 0];
  let renda = 0;
  let total = 0;
  for (let s = 0; s < 4; s++) {
    let p = (base[s] as number) + (setores[s] as number) * 0.3;
    p *= 1 + ajusteRep;
    p *= 0.8; // aprovação padrão do técnico no original
    p += rng.inteiro(Math.max(1, Math.round(sorteioMax * (s === 1 ? 5 : 1))));
    const preco = mandante.usarPrecosSugeridos ? (PRECOS_SUGERIDOS[s] as number) : (mandante.precos[s] as number);
    // a procura cai (ou sobe) em proporção ao preço relativo ao sugerido, e não em reais fixos: no original 3% por real zerava
    // o público de quem subia o camarote em R$ 40, enquanto a mesma subida no geral era pequena
    p *= Math.pow((PRECOS_SUGERIDOS[s] as number) / Math.max(1, preco), ELASTICIDADE_PRECO);
    p = Math.min(setores[s] as number, Math.max(0, Math.round(p)));
    publico[s] = p;
    total += p;
    renda += p * preco;
  }
  return { publico: publico as [number, number, number, number], total, renda };
}

// ---- Estádio -------------------------------------------------------------------------------------

export const CUSTO_EXPANSAO_POR_LUGAR = 700;
export const LUGARES_POR_EXPANSAO = 1000;
export const CAPACIDADE_MAXIMA = 120_000;

/** Custo de ampliar o estádio em `lugares` assentos. */
export function custoExpansao(lugares = LUGARES_POR_EXPANSAO): number {
  return lugares * CUSTO_EXPANSAO_POR_LUGAR;
}

/** Amplia o estádio e debita o custo do caixa; lança erro se não houver caixa ou se passar do limite. */
export function expandirEstadio(clube: Clube, lugares = LUGARES_POR_EXPANSAO): number {
  const custo = custoExpansao(lugares);
  if (clube.capacidade + lugares > CAPACIDADE_MAXIMA) throw new Error('capacidade máxima atingida');
  if (clube.caixa < custo) throw new Error('caixa insuficiente para a obra');
  clube.caixa -= custo;
  clube.balanco.estadio = (clube.balanco.estadio ?? 0) + custo;
  clube.capacidade += lugares;
  return custo;
}

// ---- Premiação -----------------------------------------------------------------------------------

export const PREMIO_POR_COLOCACAO = [5_000_000, 3_500_000, 2_000_000, 1_500_000, 1_000_000, 500_000];
export const PATROCINIO_TEMPORADA = 6_000_000;
/** Prêmio extra do título (somado ao da colocação ou da fase final): Brasileirão Série A, Copa do Brasil e Libertadores. */
export const PREMIO_CAMPEAO_SERIE_A = 40_000_000;
export const PREMIO_CAMPEAO_COPA = 20_000_000;
export const PREMIO_CAMPEAO_LIBERTADORES = 80_000_000;
export const PREMIO_CAMPEAO_SULAMERICANA = 30_000_000;

export function premioColocacao(posicao1: number): number {
  return PREMIO_POR_COLOCACAO[posicao1 - 1] ?? 0;
}

export function atualizarValores(j: Jogador, nivelClube: number): void {
  j.valor = calcularValor(j.overall, j.idade, nivelClube, j.posicao);
}
